/*
 * Copyright 2026 Bytefinger Technology (HK) Co., Limited (https://bytefinger.ai)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

export type LoginMethod = 'local' | 'ldap';

export interface OIDCMethod {
  id: string;
  name: string;
}

export interface AuthMethods {
  enable_local_auth: boolean;
  enable_ldap: boolean;
  enable_register?: boolean;
  oidc_methods: OIDCMethod[];
}

export const FALLBACK_AUTH_METHODS: AuthMethods = {
  enable_local_auth: true,
  enable_ldap: false,
  oidc_methods: [],
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function parseOIDCMethods(value: unknown): OIDCMethod[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter(
    (method): method is OIDCMethod =>
      isRecord(method) &&
      typeof method.id === 'string' &&
      typeof method.name === 'string',
  );
}

export function parseAuthMethods(payload: unknown): AuthMethods {
  const data =
    isRecord(payload) && isRecord(payload.data) ? payload.data : payload;

  if (!isRecord(data)) {
    return FALLBACK_AUTH_METHODS;
  }

  return {
    enable_local_auth: data.enable_local_auth !== false,
    enable_ldap: data.enable_ldap === true,
    enable_register:
      typeof data.enable_register === 'boolean'
        ? data.enable_register
        : undefined,
    oidc_methods: parseOIDCMethods(data.oidc_methods),
  };
}

export function isLoginMethodEnabled(
  method: LoginMethod,
  authMethods: AuthMethods,
): boolean {
  return method === 'local'
    ? authMethods.enable_local_auth
    : authMethods.enable_ldap;
}

export function getDefaultLoginMethod(
  authMethods: AuthMethods,
): LoginMethod | null {
  if (authMethods.enable_local_auth) {
    return 'local';
  }
  if (authMethods.enable_ldap) {
    return 'ldap';
  }
  return null;
}

export function getLoginEndpoint(method: LoginMethod): string {
  return method === 'ldap'
    ? '/api/auth/ldap/login'
    : '/api/passport/web/email/login/';
}
